import * as Blockly from 'blockly/core'

import { SceneDescriptor } from '../types'
import { Interpreter } from './interpreter'

/**
 * 1 オブジェクトに紐づくハンドラ群。トップレベルのハットブロックを解析して作る。
 * substack（実行する文スタックの先頭ブロック）と、それを動かす self objectId を持つ。
 */
export interface ObjectHandlers {
  readonly objectId: string
  readonly interpreter: Interpreter
  /** events_onPlay の substack 先頭ブロック群。start() で並行起動する。 */
  readonly onPlay: ReadonlyArray<Blockly.Block | null>
  /** メッセージ名 → events_onMessage の substack 先頭ブロック群。 */
  readonly onMessage: ReadonlyMap<string, ReadonlyArray<Blockly.Block | null>>
  /** events_onClicked の substack 先頭ブロック群。クリック経路用。 */
  readonly onClicked: ReadonlyArray<Blockly.Block | null>
  /** events_onTouchCharacter の substack 先頭ブロック群。接触経路用。 */
  readonly onTouchCharacter: ReadonlyArray<Blockly.Block | null>
  /**
   * 対象オブジェクト名 → events_onTouchObject の substack 先頭ブロック群。
   * 「(名前)が触れたとき」（Lua: self.Touched で hit.Name を比較）。
   */
  readonly onTouchObject: ReadonlyMap<
    string,
    ReadonlyArray<Blockly.Block | null>
  >
}

/** descriptor から name → objectId の対応表を作る。 */
export const buildNameToId = (
  descriptor: SceneDescriptor,
): ReadonlyMap<string, string> => {
  const map = new Map<string, string>()
  for (const obj of descriptor.objects) {
    map.set(obj.name, obj.id)
  }
  return map
}

/** descriptor から objectId → name の対応表を作る（接触ビヘイビアの逆引き用）。 */
export const buildIdToName = (
  descriptor: SceneDescriptor,
): ReadonlyMap<string, string> => {
  const map = new Map<string, string>()
  for (const obj of descriptor.objects) {
    map.set(obj.id, obj.name)
  }
  return map
}

/**
 * 1 つのワークスペースのトップレベルブロックを解析し、ハンドラを構築する。
 * ハットブロック以外のトップレベルブロック（浮いた文など）は実行対象にしない。
 */
export const buildHandlers = (
  objectId: string,
  interpreter: Interpreter,
  workspace: Blockly.Workspace,
): ObjectHandlers => {
  const onPlay: Array<Blockly.Block | null> = []
  const onClicked: Array<Blockly.Block | null> = []
  const onTouchCharacter: Array<Blockly.Block | null> = []
  const onMessage = new Map<string, Array<Blockly.Block | null>>()
  const onTouchObject = new Map<string, Array<Blockly.Block | null>>()

  for (const block of workspace.getTopBlocks(false)) {
    if (!block.isEnabled()) {
      continue
    }
    switch (block.type) {
      case 'events_onPlay':
        onPlay.push(block.getInputTargetBlock('SUBSTACK'))
        break
      case 'events_onClicked':
        onClicked.push(block.getInputTargetBlock('SUBSTACK'))
        break
      case 'events_onTouchCharacter':
        onTouchCharacter.push(block.getInputTargetBlock('SUBSTACK'))
        break
      case 'events_onMessage': {
        const message = block.getFieldValue('VAL')
        if (typeof message !== 'string' || message.length === 0) {
          console.warn(
            `[preview] events_onMessage にメッセージ名がありません: ${block.id}`,
          )
          break
        }
        const handlers = onMessage.get(message) ?? []
        handlers.push(block.getInputTargetBlock('SUBSTACK'))
        onMessage.set(message, handlers)
        break
      }
      case 'events_onTouchObject': {
        const targetName = block.getFieldValue('VAL')
        if (typeof targetName !== 'string' || targetName.length === 0) {
          console.warn(
            `[preview] events_onTouchObject に対象オブジェクト名がありません: ${block.id}`,
          )
          break
        }
        const handlers = onTouchObject.get(targetName) ?? []
        handlers.push(block.getInputTargetBlock('SUBSTACK'))
        onTouchObject.set(targetName, handlers)
        break
      }
      default:
        // 残りの物理/Humanoid トリガや未知のハットは graceful skip。
        if (block.type.startsWith('events_')) {
          console.warn(
            `[preview] 未対応のイベントブロックをスキップ: ${block.type} (${block.id})`,
          )
        }
        break
    }
  }

  return {
    objectId,
    interpreter,
    onPlay,
    onMessage,
    onClicked,
    onTouchCharacter,
    onTouchObject,
  }
}
